import { Fragment, useMemo } from 'react';
import { View } from 'react-native';
import { useSelector } from 'react-redux';
import { Divider, Icon, useTheme } from '@ui-kitten/components';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import lodashIsEmpty from 'lodash/isEmpty';

import CalendarIcon from '../../../Assets/calendar.svg';
import MoneyIcon from '../../../Assets/money.svg';
import ListItem from '../../../Components/ListItem';
import {
  formikNames,
  textCategory,
} from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import TicketIcon from '../../../Components/SVG/Tickets';
import constants from '../../../Config/constants';
import { pluralize } from '../../../Helper';
import { getCurrency } from '../../../Helper/Routines';
import { ExtraGroupItem } from '../../../RTK/api/types';
import { selectReservationCheckoutData } from '../../../RTK/utility/selectors';
import themeStyle from '../../../Theme/styles';

import { ReservationCheckout } from '../types';

type SummaryDataType = Pick<
  ReservationCheckout,
  'address' | 'itemName' | 'selectedDate' | 'selectedTime' | 'size'
>;
type SummaryType = {
  data?: SummaryDataType & {
    addOns?: ExtraGroupItem[]; //
    totalAmount?: number; //
  }; // this is pass on my reservation details
  isReservation: boolean;
  withAddress?: boolean;
  withCostBreakdown?: boolean;
  withPrice?: boolean;
  withSeparator?: boolean;
};
const Summary = ({
  data,
  isReservation,
  withAddress,
  withCostBreakdown,
  withPrice,
  withSeparator,
}: SummaryType) => {
  const theme = useTheme();
  const formikResult = useFormikContext(); // this will throw warning on order details page, its ok as expected
  const selectorData = useSelector(selectReservationCheckoutData);
  const { values } = useMemo(
    () => (data ? { values: {} } : formikResult),
    [data, formikResult],
  );
  const isOrderDetails = Boolean(data?.totalAmount);
  const addOnsValue = useMemo(() => {
    if (!lodashIsEmpty(data?.addOns)) return data.addOns;
    return values[formikNames.ADDONS];
  }, [data?.addOns, values]);
  const summaryData: SummaryDataType = data || selectorData;
  const itemPrice = addOnsValue?.[0]?.price;
  const addOnName = addOnsValue?.[0]?.name;
  const totalAddOnPrice = useMemo(
    () =>
      addOnsValue?.reduce?.(
        (acc: number, val: SummaryType['data']['addOns'][0]) =>
          acc + Number(val.price),
        0,
      ),
    [addOnsValue],
  );
  const amountDue = useMemo(() => {
    if (isOrderDetails) {
      return data.totalAmount;
    }
    return totalAddOnPrice * summaryData.size;
  }, [data?.totalAmount, isOrderDetails, summaryData.size, totalAddOnPrice]);

  const list = useMemo(() => {
    if (lodashIsEmpty(summaryData)) return [];
    return [
      {
        name: dayjs(summaryData.selectedDate).format('MMMM DD, YYYY'),
        icon: { leftIcon: { svg: CalendarIcon, size: 20 } },
      },
      {
        name: dayjs(summaryData.selectedTime, constants.DBTIME_FORMAT).format(
          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
        ),
        icon: { leftIcon: { name: 'clock-outline', size: 20 } },
      },
      ...(!isReservation && withPrice
        ? [
            {
              name: `${getCurrency(itemPrice)} per ticket`,
              icon: { leftIcon: { svg: MoneyIcon, size: 20 } },
            },
          ]
        : []),
      {
        name: `${summaryData.size} ${pluralize(
          summaryData.size,
          isReservation ? 'seat' : 'ticket',
        )}${addOnName ? ` - ${addOnName}` : ''}`,
        icon: { leftIcon: { svg: TicketIcon, size: 22 } },
      },
      ...(withAddress && summaryData.address
        ? [
            {
              name: summaryData.address,
              icon: { leftIcon: { name: 'pin-outline' } },
            },
          ]
        : []),
    ];
  }, [
    addOnName,
    isReservation,
    itemPrice,
    summaryData,
    withAddress,
    withPrice,
  ]);

  if (lodashIsEmpty(summaryData)) return null;

  return (
    <Fragment>
      {list.map((l, i) => (
        <Fragment key={l.name}>
          <ListItem
            contentContainerStyle={themeStyle.pageVerticalSpacingMedium}
            title={
              <ResponsiveText category={textCategory.BODY}>
                {l.name}
              </ResponsiveText>
            }
            {...l.icon}
          />
          {withSeparator && i !== list.length - 1 && (
            <Divider style={themeStyle.divider} />
          )}
        </Fragment>
      ))}
      {withCostBreakdown && (
        <Fragment>
          <Divider style={themeStyle.divider} />
          <ListItem
            contentContainerStyle={themeStyle.pageVerticalSpacingMedium}
            title={
              <ResponsiveText category={textCategory.BODY}>
                {summaryData.itemName} &times; {summaryData.size}
              </ResponsiveText>
            }
            accessoryRight={
              isOrderDetails ? null : (
                <ResponsiveText category={textCategory.BODY}>
                  {getCurrency(itemPrice)}
                </ResponsiveText>
              )
            }
          />
          <Divider style={themeStyle.divider} />
          <ListItem
            contentContainerStyle={themeStyle.spacingTopMedium}
            title={
              <Fragment>
                <View style={themeStyle.flexDirectionRowCenterSpaceBetween}>
                  <ResponsiveText
                    category={textCategory.BODY}
                    style={themeStyle.bold}
                  >
                    Total Amount {isOrderDetails ? 'Paid' : 'Due'}
                  </ResponsiveText>
                  <ResponsiveText
                    category={textCategory.BODY}
                    style={themeStyle.bold}
                  >
                    {getCurrency(amountDue)}
                  </ResponsiveText>
                </View>
                {!isOrderDetails && isReservation && (
                  <Fragment>
                    <ResponsiveText
                      category={textCategory.XSMALL_BODY}
                      style={[themeStyle.italic, themeStyle.spacingBottomSmall]}
                    >
                      The amount will be offset against your bill.
                    </ResponsiveText>
                    <View style={themeStyle.flexDirectionRowCenter}>
                      <Icon
                        name="info-outline"
                        fill={theme['icon_color']}
                        style={[
                          { height: 20, width: 20 },
                          themeStyle.spacingRightSmall,
                        ]}
                      />
                      <ResponsiveText category={textCategory.XSMALL_BODY}>
                        All menu prices are exclusive of VAT and 10% Service
                        Charge.
                      </ResponsiveText>
                    </View>
                  </Fragment>
                )}
              </Fragment>
            }
          />
        </Fragment>
      )}
    </Fragment>
  );
};

export default Summary;
